import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { reproducirAlerta } from '../../utils/sonidoAlerta';

const COLORES = {
  critica: { bg: '#5c0a0a', borde: '#e74c3c', badge: '#e74c3c' },
  alta:    { bg: '#5c3209', borde: '#e67e22', badge: '#e67e22' },
  media:   { bg: '#5c5209', borde: '#f1c40f', badge: '#f1c40f' },
  baja:    { bg: '#0a3d1f', borde: '#27ae60', badge: '#27ae60' },
};

const ICONOS = { critica: '🔴', alta: '🟠', media: '🟡', baja: '🟢' };

const DURACION_MS = 25000;

function formatHora(ts) {
  return new Date(ts).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
}

export default function ToastAlerta({ incidente, onClose, conSonido }) {
  const [visible,  setVisible]  = useState(true);
  const [progreso, setProgreso] = useState(100);
  const onCloseRef = React.useRef(onClose);

  const prioridad = incidente?.prioridad || 'media';
  const colores   = COLORES[prioridad] || COLORES.media;
  const esNuevo   = incidente?._tipo === 'nuevo';

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (conSonido) reproducirAlerta(prioridad, DURACION_MS / 1000);

    const inicio = Date.now();
    const intervalo = setInterval(() => {
      const elapsed = Date.now() - inicio;
      setProgreso(Math.max(0, 100 - (elapsed / DURACION_MS) * 100));
    }, 50);

    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onCloseRef.current(), 300);
    }, DURACION_MS);

    return () => { clearTimeout(t); clearInterval(intervalo); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      width: 380, borderRadius: 12,
      background: colores.bg,
      border: `2px solid ${colores.borde}`,
      boxShadow: '0 10px 32px rgba(0,0,0,.55)',
      overflow: 'hidden',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity .3s, transform .3s',
    }}>

      {/* Barra de progreso */}
      <div style={{
        height: 4, background: 'rgba(255,255,255,.15)',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          width: `${progreso}%`,
          background: colores.borde,
          transition: 'width .05s linear',
        }} />
      </div>

      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>

        {/* Encabezado */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', letterSpacing: '.3px' }}>
            🚨 {esNuevo ? 'NUEVO INCIDENTE' : 'INCIDENTE ACTUALIZADO'}
          </span>
          <button onClick={() => { setVisible(false); setTimeout(onClose, 300); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.55)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>
            ✕
          </button>
        </div>

        {/* Prioridad + hora */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            background: colores.badge, color: '#fff',
            fontWeight: 700, fontSize: '.72rem', letterSpacing: '.6px',
            padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase',
          }}>
            {ICONOS[prioridad]} {prioridad}
          </span>
          {incidente?._ts && (
            <span style={{ color: 'rgba(255,255,255,.55)', fontSize: '.75rem' }}>
              {formatHora(incidente._ts)}
            </span>
          )}
        </div>

        {/* Tipo / Clase de incidente */}
        {(incidente?.tipo || incidente?.Des_Tipo_Servicio || incidente?.Des_Clase_Servicio) && (
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '.92rem' }}>
            {incidente.tipo || incidente.Des_Tipo_Servicio || incidente.Des_Clase_Servicio}
          </div>
        )}
        {incidente?.Des_Clase_Servicio && incidente?.Des_Tipo_Servicio && (
          <div style={{ color: 'rgba(255,255,255,.75)', fontSize: '.8rem' }}>
            🏷️ {incidente.Des_Clase_Servicio}
          </div>
        )}

        {/* Descripción / Observaciones */}
        {incidente?.descripcion && (
          <div style={{
            color: 'rgba(255,255,255,.9)', fontSize: '.83rem',
            background: 'rgba(0,0,0,.2)', borderRadius: 6, padding: '6px 10px',
            lineHeight: 1.45,
          }}>
            📋 {incidente.descripcion}
          </div>
        )}

        {/* Dirección */}
        {incidente?.direccion && (
          <div style={{ color: 'rgba(255,255,255,.85)', fontSize: '.83rem', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span>📍</span>
            <span style={{ lineHeight: 1.4 }}>{incidente.direccion}</span>
          </div>
        )}

        {/* Unidades + Solicitante */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {incidente?.unidades && (
            <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>
              🚒 {incidente.unidades}
            </div>
          )}
          {incidente?.solicitante && (
            <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '.78rem' }}>
              👤 {incidente.solicitante}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

ToastAlerta.propTypes = {
  incidente: PropTypes.object.isRequired,
  onClose:   PropTypes.func.isRequired,
  conSonido: PropTypes.bool,
};

